import { Zap, Heart, Activity } from 'lucide-react';

export function CardioPrescriptionDetails({ cardioBlock }) {
  if (!cardioBlock) return null;
  // A mix lists every movement in CardioMixCard — one segment's numbers here
  // would contradict the block total.
  if (cardioBlock.rounds_segments?.length > 1) return null;

  const rp_rounds = cardioBlock.rounds_protocol;
  if (rp_rounds) {
    return (
      <div className="space-y-2" data-testid="cardio-rounds-details">
        <p className="text-xs font-semibold text-accent flex items-center gap-1.5">
          <Zap size={11} /> {rp_rounds.protocol_name}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <span className="font-mono bg-accent-deep text-accent/90 px-1.5 py-0.5 rounded">{rp_rounds.work_duration_seconds}s work</span>
          <span>/</span>
          <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{rp_rounds.rest_duration_seconds}s rest</span>
          <span className="text-muted-foreground/60">×</span>
          <span className="font-mono font-semibold">{rp_rounds.sets} rounds</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-accent">Work RPE {rp_rounds.work_rpe}</span>
          <span className="text-muted-foreground/40">|</span>
          <span className="text-muted-foreground">{rp_rounds.total_duration_minutes} min total</span>
        </div>
      </div>
    );
  }

  const ip = cardioBlock.interval_protocol;
  const sp = cardioBlock.steady_prescription;
  const rp = cardioBlock.recovery_prescription;

  if (ip) {
    return (
      <div className="space-y-2" data-testid="cardio-interval-details">
        <p className="text-xs font-semibold text-orange-400 flex items-center gap-1.5">
          <Zap size={11} /> {ip.protocol_name}
        </p>
        {ip.description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{ip.description}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <span className="font-mono bg-orange-500/10 text-orange-400 px-1.5 py-0.5 rounded">{ip.work_duration_seconds}s work</span>
          <span>/</span>
          <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{ip.rest_duration_seconds}s rest</span>
          <span className="text-muted-foreground/60">×</span>
          <span className="font-mono font-semibold">{ip.rounds} rounds</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {ip.warmup_minutes > 0 && <span>Warmup {ip.warmup_minutes}m</span>}
          {ip.warmup_minutes > 0 && <span className="text-orange-400/60">→</span>}
          <span className="text-orange-400 font-medium">Intervals</span>
          {ip.cooldown_minutes > 0 && <span className="text-orange-400/60">→</span>}
          {ip.cooldown_minutes > 0 && <span>Cooldown {ip.cooldown_minutes}m</span>}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-orange-400">Push RPE {ip.work_rpe}</span>
          <span className="text-muted-foreground/40">|</span>
          <span className="text-green-400">Recover RPE {ip.rest_rpe}</span>
        </div>
      </div>
    );
  }

  if (sp) {
    const styleName = sp.target_style?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const hints = [sp.cadence_or_speed_hint, sp.incline_hint, sp.resistance_hint].filter(Boolean);
    return (
      <div className="space-y-2" data-testid="cardio-steady-details">
        <p className="text-xs font-semibold text-accent">{styleName}</p>
        {sp.style_description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{sp.style_description}</p>
        )}
        {hints.length > 0 && (
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {hints.map((h, i) => (
              <span key={i} className="font-mono bg-accent-deep text-accent/90 px-1.5 py-0.5 rounded">{h}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (rp) {
    const hints = [rp.cadence_or_speed_hint, rp.incline_hint].filter(Boolean);
    return (
      <div className="space-y-2" data-testid="cardio-recovery-details">
        <p className="text-xs font-semibold text-blue-400 flex items-center gap-1.5">
          <Heart size={11} /> Active Recovery
        </p>
        {rp.style_description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{rp.style_description}</p>
        )}
        {hints.length > 0 && (
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {hints.map((h, i) => (
              <span key={i} className="font-mono bg-blue-400/10 text-blue-300 px-1.5 py-0.5 rounded">{h}</span>
            ))}
          </div>
        )}
        {rp.coaching_note && (
          <p className="text-xs text-blue-300/70 italic">{rp.coaching_note}</p>
        )}
      </div>
    );
  }

  return null;
}
